import { Icon, type IconName } from '@/shared/ui/Icon';
import { Link, useLocation } from 'react-router-dom';

export interface MeetingSubTabItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  activeWhen?: (pathname: string) => boolean;
}

interface MeetingSubTabsProps {
  ariaLabel: string;
  items: MeetingSubTabItem[];
}

function isActive(pathname: string, item: MeetingSubTabItem) {
  if (item.activeWhen?.(pathname)) return true;
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function MeetingSubTabs({ ariaLabel, items }: MeetingSubTabsProps) {
  const { pathname } = useLocation();

  return (
    <nav
      aria-label={ariaLabel}
      className="relative grid gap-1.5 overflow-hidden rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-1.5"
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      <div
        className="pointer-events-none absolute -right-10 -top-12 h-28 w-28 rounded-full bg-primary-soft/80 blur-xl"
        aria-hidden="true"
      />
      {items.map((item) => {
        const active = isActive(pathname, item);
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            className={`pressable relative flex min-h-[46px] items-center justify-center gap-2 rounded-[14px] px-2.5 text-center text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:shadow-focus-ring sm:gap-2 ${
              active
                ? 'bg-primary text-white'
                : 'bg-surface-muted/70 text-ink-muted hover:text-ink'
            }`}
          >
            <Icon name={item.icon} size={16} className="shrink-0" />
            <span className="truncate">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
